import {toInt} from '@/library/math';
import {ROUTE_REF_PARAM_DEFINITION} from '@/utils/portuna';
import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';

const TaskList = () => import('../views/task/TaskList.vue');
const TaskView = () => import('../views/task/TaskView.vue');
const TaskEdit = () => import('../views/task/TaskEdit.vue');

const TaskTemplateEdit = () => import('../views/task/taskTemplate/TaskTemplateEdit.vue');
const TaskTemplateList = () => import('../views/task/taskTemplate/components/TaskTemplateList.vue');

const TaskSettings          = () => import('../views/task/TaskSettings.vue');
const TaskSettingsGeneral   = () => import('../views/task/settings/TaskSettingsGeneral.vue');
const TaskSettingsDueDate   = () => import('../views/task/settings/TaskSettingsDueDate.vue');

const TaskSettingsAdminCategories = () => import('../views/task/settings/TaskSettingsAdminCategories.vue');
const TaskSettingsUserCategories  = () => import('../views/task/settings/TaskSettingsUserCategories.vue');

let meta: MetaData = {module: 'task'};

let startParam = ':start(|0|\\d{4}-\\d{2}-\\d{2})?';

let task: RouteRecordRaw[] = [
    {
        path: '/task',
        alias: '/task/entry',
        redirect: {name: 'task-list-mine'},
        meta,
    },
    {
        path: '/task/entry/mine/:state(open|resolved|all)?/:tab?',
        name: 'task-list-mine',
        component: TaskList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: 'mine',
                state: route.params.state,
                tab: route.params.tab,
            };
        },
    },
    {
        path: '/task/entry/all/:state(open|resolved|all)?/:tab?',
        name: 'task-list-all',
        component: TaskList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: 'all',
                state: route.params.state,
                tab: route.params.tab,
            };
        },
    },
    {
        path: '/task/entry/all/entity/:entityId(\\d+)',
        name: 'task-list-all-entity',
        component: TaskList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: 'all',
                tab: 'entity',
                entityId: parseInt(route.params.entityId),
            };
        },
    },
    {
        path: '/task/entry/traced/:state(open|resolved|all)?/:tab?',
        name: 'task-list-traced',
        component: TaskList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: 'traced',
                state: route.params.state,
                tab: route.params.tab,
            };
        },
    },
    {
        path: '/task/entry/view/:id(\\d+)/' + startParam + '/:tab(overview|relatives|notes|reminders|info)?/' + ROUTE_REF_PARAM_DEFINITION,
        name: 'task-view',
        component: TaskView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                start: route.params.start ? route.params.start : '0',
            };
        },
    },
    {
        path: '/task/entry/edit/:id(\\d+)/' + startParam + '/' + ROUTE_REF_PARAM_DEFINITION + '/:fromView(1|0)?',
        name: 'task-edit',
        component: TaskEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                start: route.params.start ? route.params.start : '0',
                fromView: parseInt(route.params.fromView),
            };
        },
    },
    {
        path: '/task/entry/copy/:id(\\d+)/' + startParam + '/' + ROUTE_REF_PARAM_DEFINITION + '/:fromView(1|0)?',
        name: 'task-copy',
        component: TaskEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                start: route.params.start,
                mode: 'copy',
                fromView: parseInt(route.params.fromView),
            };
        },
    },
    {
        path: '/task/entry/add/:parent(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION + '/:fromView(1|0)?',
        name: 'task-add',
        component: TaskEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: 0,
                parent: parseInt(route.params.parent),
                mode: 'add',
                fromView: parseInt(route.params.fromView),
            };
        },
    },
    {
        path: '/task/entry/add/employee/:employeeId(\\d+)/' + ROUTE_REF_PARAM_DEFINITION,
        name: 'task-employee-add',
        component: TaskEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: 0,
                employeeId: parseInt(route.params.employeeId),
                mode: 'add',
            };
        },
    },
    {
        path: '/task/entry/add/template/:templateId(\\d+)/:parent(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        name: 'task-add-apply-template',
        component: TaskEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: 0,
                templateId: parseInt(route.params.templateId),
                parent: parseInt(route.params.parent),
                mode: 'add',
            };
        },
    },
    {
        name: 'task-template-add',
        path: '/task/task-template/add',
        component: TaskTemplateEdit,
        meta,
        props: {id: 0},
    },
    {
        name: 'task-template-edit',
        path: '/task/task-template/edit/id/:id',
        component: TaskTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
            };
        },
    },
    {
        path: '/task/settings/:route(intranet)?',
        redirect: {name: 'task-settings-general'},
        name: 'task-settings',
        component: TaskSettings,
        meta,
        children: [
            {
                path: 'general/' + ROUTE_REF_PARAM_DEFINITION,
                name: 'task-settings-general',
                component: TaskSettingsGeneral,
                meta,
            },
            {
                path: 'due-date/' + ROUTE_REF_PARAM_DEFINITION,
                name: 'task-settings-due-date',
                component: TaskSettingsDueDate,
                meta,
            },
            {
                path: 'templates/' + ROUTE_REF_PARAM_DEFINITION,
                name: 'task-settings-templates',
                component: TaskTemplateList,
                meta,
            },
            {
                path: 'user-categories/' + ROUTE_REF_PARAM_DEFINITION,
                name: 'task-settings-user-categories',
                component: TaskSettingsUserCategories,
                meta,
            },
            {
                path: 'admin-categories/' + ROUTE_REF_PARAM_DEFINITION,
                name: 'task-settings-admin-categories',
                component: TaskSettingsAdminCategories,
                meta,
            },
        ],
    },

    // COMPATIBILITY: OLD PATHS
    {
        path: '/task/entry/index/tab-query/entity/id/:entityId',
        redirect: {name: 'task-list-all-entity'},
    },
    {
        path: '/task/entry/edit/id/:id/start/' + startParam + '/copy/1/:wildcard*',
        redirect: {name: 'task-copy'},
    },
    {
        path: '/task/entry/edit/id/:id/copy/1/:wildcard*',
        redirect: {name: 'task-copy'},
    },
    {
        path: '/task/entry/edit/id/:id/start/' + startParam + '/:wildcard*',
        redirect: {name: 'task-edit'},
    },
    {
        path: '/task/entry/edit/id/:id/:wildcard*',
        redirect: {name: 'task-edit'},
    },
    {
        path: '/task/entry/view/id/:id/start/' + startParam + '/:wildcard*',
        redirect: {name: 'task-view'},
    },
    {
        path: '/task/entry/view/id/:id/:wildcard*',
        redirect: {name: 'task-view'},
    },
    // @todo: remove in a few weeks
    {
        path: '/task/entry/mine/range/1/resolved/2',
        redirect: {
            name: 'task-list-mine',
            params: {tab: 'mine_today'},
        },
    },
    // @todo: remove in a few weeks
    {
        path: '/task/entry/traced/range/1/resolved/2',
        redirect: {
            name: 'task-list-traced',
            params: {tab: 'traced_today'},
        },
    },
    {
        path: '/task/entry/mine/:wildcard*',
        redirect: {name: 'task-list-mine'},
    },
    {
        path: '/task/entry/mine/:wildcard*',
        redirect: {name: 'task-list-mine'},
    },
    {
        path: '/task/entry/traced/:wildcard*',
        redirect: {name: 'task-list-traced'},
    },
    // @todo: maybe 404?
    // all other task routes redirect to task-list
    {
        path: '/task/:wildcard*',
        redirect: {name: 'task-list-mine'},
    },
];

export default task;
